import P2Regular from 'components/typography/P2Regular';
import SectionColumn from 'components/tables/data/evidence-field/columns/SectionColumn';
import EvidenceFieldSectionSelectFilter from 'components/tables/filters/EvidenceFieldSectionSelectFilter';
import { EvidenceSection } from 'components/forms/evidence-field/use-evidence-sections';
import { ColDef } from 'ag-grid-community';
import { useApi } from 'api/api-context';
import ActionsColumn from 'components/tables/data/evidence-field/columns/ActionsColumn';
import DeviceColumn from 'components/tables/data/evidence-field/columns/DeviceColumn';
import EvidenceFieldIdColumn from 'components/tables/data/evidence-field/columns/EvidenceFieldIdColumn';
import FieldNameColumn from 'components/tables/data/evidence-field/columns/FieldNameColumn';
import FieldTypeColumn from 'components/tables/data/evidence-field/columns/FieldTypeColumn';
import IsRequiredColumn from 'components/tables/data/evidence-field/columns/IsRequiredColumn';
import StructureColumn from 'components/tables/data/evidence-field/columns/StructureColumn';
import ApplicabilityColumn from 'components/tables/data/evidence-field/columns/ApplicabilityColumn';
import { EvidenceField, EvidenceFieldRow } from 'components/tables/data/evidence-field/types';
import DataTable from 'components/tables/DataTable';
import EvidenceFieldRequiredSelectFilter from 'components/tables/filters/EvidenceFieldRequiredSelectFilter';
import EvidenceFieldTypeSelectFilter from 'components/tables/filters/EvidenceFieldTypeSelectFilter';
import EvidenceFieldApplicabilitySelectFilter from 'components/tables/filters/EvidenceFieldApplicabilitySelectFilter';
import {
  EvidenceFieldAvailabilityColumnFilter,
  EvidenceFieldAvailabilityFloatingFilter,
  EvidenceFieldDeviceRestrictionColumnFilter,
  EvidenceFieldDeviceRestrictionFloatingFilter,
} from 'components/tables/filters/EvidenceFieldScopeFilter';
import { useCallback, useMemo } from 'react';
import { ADD_EVIDENCE_FIELD_PAGE_PATH } from 'routes/paths';
import { getResponsiveActionsColumnWidth, useResponsiveDesign } from 'hooks/use-responsive-design';

/**
 * Table which displays organizations.
 */
export default function OrganizationTable() {
  const { getAction } = useApi();
  const { tableActionMode } = useResponsiveDesign();

  const createRow = useCallback(
    (evidenceField: EvidenceField, sections: EvidenceSection[]): EvidenceFieldRow => ({
      ...evidenceField,
      sectionName:
        sections.find((section) => section.sectionId === evidenceField.sectionId)?.name ??
        String(evidenceField.sectionId),
    }),
    []
  );

  const action = useCallback(async () => {
    const getEvidenceFields = getAction('DeviceListAllEvidenceFields');
    const [evidenceFields, sections] = await Promise.all([
      getEvidenceFields(),
      getAction('DeviceListEvidenceFieldSections')({}),
    ]);
    return evidenceFields.map((field) => createRow(field, sections));
  }, [getAction]);

  const dataExport = useMemo(
    () => ({
      modalTitle: 'Exportovať evidenčné polia',
      fileName: 'evidenčné-polia.xlsx',
      columnKeys: ['evidenceFieldId', 'fieldName', 'fieldType', 'isRequired', 'applicability'],
    }),
    []
  );

  const columns = useMemo<ColDef[]>(
    () => [
      // ANCHOR Visible Columns
      {
        field: 'evidenceFieldId',
        headerName: 'ID',
        sortable: true,
        unSortIcon: true,
        width: 76,
        minWidth: 76,
        maxWidth: 76,
        resizable: false,
        autoHeaderHeight: true,
        cellRenderer: EvidenceFieldIdColumn,
      },
      {
        field: 'fieldName',
        headerName: 'Názov',
        minWidth: 250,
        sortable: true,
        unSortIcon: true,
        autoHeaderHeight: true,
        cellRenderer: FieldNameColumn,
      },
      {
        field: 'fieldType',
        headerName: 'Typ',
        minWidth: 150,
        sortable: true,
        unSortIcon: true,
        filter: true,
        floatingFilter: true,
        floatingFilterComponent: EvidenceFieldTypeSelectFilter,
        autoHeaderHeight: true,
        cellRenderer: FieldTypeColumn,
      },
      {
        field: 'isRequired',
        valueGetter: ({ data }) => (data?.isRequired ? 'y' : 'n'),
        headerName: 'Povinné',
        minWidth: 120,
        filter: true,
        floatingFilter: true,
        floatingFilterComponent: EvidenceFieldRequiredSelectFilter,
        autoHeaderHeight: true,
        cellRenderer: IsRequiredColumn,
      },
      {
        field: 'applicability',
        headerName: 'Použitie',
        minWidth: 180,
        filter: true,
        floatingFilter: true,
        floatingFilterComponent: EvidenceFieldApplicabilitySelectFilter,
        valueGetter: ({ data }) => (data?.appliesToPeople ? 'people' : 'devices'),
        autoHeaderHeight: true,
        cellRenderer: ApplicabilityColumn,
      },
      {
        field: 'structure',
        headerName: 'Dostupnosť',
        minWidth: 300,
        filter: EvidenceFieldAvailabilityColumnFilter,
        floatingFilter: true,
        floatingFilterComponent: EvidenceFieldAvailabilityFloatingFilter,
        autoHeaderHeight: true,
        cellRenderer: StructureColumn,
      },
      {
        field: 'device',
        headerName: 'Vymedzenie zariadení',
        minWidth: 300,
        filter: EvidenceFieldDeviceRestrictionColumnFilter,
        floatingFilter: true,
        floatingFilterComponent: EvidenceFieldDeviceRestrictionFloatingFilter,
        autoHeaderHeight: true,
        cellRenderer: DeviceColumn,
      },

      {
        field: 'sectionId',
        headerName: 'Sekcia',
        minWidth: 220,
        sortable: true,
        unSortIcon: true,
        filter: true,
        floatingFilter: true,
        floatingFilterComponent: EvidenceFieldSectionSelectFilter,
        valueGetter: ({ data }) => String(data?.sectionId ?? ''),
        valueFormatter: ({ data }) => data?.sectionName ?? '',
        getQuickFilterText: ({ data }) => data?.sectionName ?? '',
        cellRenderer: SectionColumn,
      },
      {
        field: 'order',
        headerName: 'Poradie',
        minWidth: 110,
        sortable: true,
        unSortIcon: true,
        cellRenderer: ({ value }: { value: number }) => <P2Regular>{value}</P2Regular>,
      },

      // ANCHOR Actions
      {
        field: '_actions',
        headerName: 'Akcie',
        pinned: 'right',
        width: getResponsiveActionsColumnWidth(194, tableActionMode),
        minWidth: getResponsiveActionsColumnWidth(194, tableActionMode),
        maxWidth: getResponsiveActionsColumnWidth(194, tableActionMode),
        lockPosition: 'right',
        autoHeaderHeight: true,
        cellRenderer: ActionsColumn,
      },
    ],
    [tableActionMode]
  );

  return (
    <DataTable
      tableId="evidence-fields"
      title="Evidenčné polia"
      addButtonText="Pridať evidenčné pole"
      addButtonTarget={ADD_EVIDENCE_FIELD_PAGE_PATH.original}
      hideToggleDiscarded
      columns={columns}
      action={action}
      dataExport={dataExport}
    />
  );
}
